import { useEffect, useState } from "react";
import { matchPath, useLocation } from "react-router";
import { shallowEqual, useSelector } from "react-redux";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import { AppState } from "@Store/index";
import { withLang } from "@Utils/languageCore";
import getCookie from "helpers/getCookie";
import setCookie from "helpers/setCookie";
import config from "@Config/index";

interface UseUcasModalTriggerResult {
  shouldShow: boolean;
  markSeen: () => void;
}

const COOKIE_NAME = "ucasAnnouncementSeen";
const GB_COUNTRY_CODE = "GB";

// withLang() also yields the /:lang-prefixed variants, so locale URLs
// (e.g. /en/places/:id) match without listing them here.
const HOME_PATHS = [...withLang("/")];
const SEARCH_PATHS = [...withLang("/places/search/:id")];
const PROPERTY_PATHS = [...withLang("/places/:id")];

const matchesAny = (pathname: string, paths: string[]): boolean =>
  paths.some((path) => !!matchPath(pathname, { path, exact: true }));

const hasSeenBefore = (): boolean => {
  try {
    return getCookie(COOKIE_NAME) === "true";
  } catch {
    // Cookie unavailable — fail safe by treating it as already seen
    // so we never loop the modal on every navigation.
    return true;
  }
};

const useUcasModalTrigger = (): UseUcasModalTriggerResult => {
  const [shouldShow, setShouldShow] = useState(false);
  const { pathname } = useLocation();
  const isFeatureEnabled =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_UCAS_ANNOUNCEMENT_MODAL) ||
    config.IS_CHINA_APP;

  const { searchCountry, propertyCountry } = useSelector(
    ({ searchPageData, propertyPageData }: AppState) => ({
      searchCountry: searchPageData.region?.location?.country?.short_name,
      propertyCountry: propertyPageData.data?.location?.country?.short_name,
    }),
    shallowEqual,
  );

  const markSeen = (): void => {
    setShouldShow(false);
    try {
      setCookie({ cname: COOKIE_NAME, cvalue: "true", exdays: 10 });
    } catch {
      // Ignore — dismissal still applies for this page view via local state.
    }
  };

  useEffect(() => {
    if (!isFeatureEnabled || hasSeenBefore()) {
      setShouldShow(false);
      return;
    }

    // Home: show for everyone.
    if (matchesAny(pathname, HOME_PATHS)) {
      setShouldShow(true);
      return;
    }

    // Search / Property: GB only. Wait for country to resolve so we don't
    // flash the modal on non-UK pages before region/property data loads.
    // Exact matching throughout: a prefix match would also fire on nested
    // routes such as /places/search/:id/agent-view or /places/:id/video/:videoId.
    if (matchesAny(pathname, SEARCH_PATHS)) {
      setShouldShow(searchCountry === GB_COUNTRY_CODE);
      return;
    }

    if (matchesAny(pathname, PROPERTY_PATHS)) {
      setShouldShow(propertyCountry === GB_COUNTRY_CODE);
      return;
    }

    setShouldShow(false);
  }, [pathname, isFeatureEnabled, searchCountry, propertyCountry]);

  return { shouldShow, markSeen };
};

export default useUcasModalTrigger;
